import {
  SwitchProps as MUISwitchProps,
  Stack,
  Typography,
} from '@mui/material';
import { ReactNode, useCallback } from 'react';
import {
  Control,
  Controller,
  FieldValues,
  Path,
  RegisterOptions,
} from 'react-hook-form';

import { getDataTestId } from '@/utils/browser';
import { getObjectFieldByPath } from '@/utils/string';

import { StyledSwitch } from './Form.styled';

type SwitchProps<FormPayload extends FieldValues> = {
  control?: Control<FormPayload>;
  label?: string | ReactNode;
  name?: Path<FormPayload>;
  rules?: Omit<
    RegisterOptions<FormPayload, Path<FormPayload>>,
    'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled'
  >;
  labelPosition?: 'left' | 'right';
} & Omit<MUISwitchProps, 'variant' | 'name'>;

export function Switch<FormPayload extends FieldValues>({
  control,
  name,
  rules,
  label,
  value,
  onChange,
  onBlur,
  labelPosition = 'left',
  ...props
}: SwitchProps<FormPayload>) {
  const render = useCallback(
    (
      value: MUISwitchProps['value'],
      onChange: MUISwitchProps['onChange'],
      onBlur: MUISwitchProps['onBlur'],
      defaultValue?: MUISwitchProps['value'],
    ) => (
      <Stack gap={1.5} justifyContent={'space-between'}>
        {labelPosition === 'left' && label && (
          <Typography component="span" variant="body2">
            {label}
          </Typography>
        )}
        <StyledSwitch
          {...getDataTestId(`${label}-switch`)}
          checked={
            typeof value === 'boolean'
              ? value
              : (defaultValue as boolean) || false
          }
          onChange={onChange}
          onBlur={onBlur}
          {...props}
        />
        {labelPosition === 'right' && label && (
          <Typography component="span" variant="body2">
            {label}
          </Typography>
        )}
      </Stack>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [label, props],
  );

  if (control && name) {
    return (
      <Controller
        rules={rules}
        control={control}
        name={name}
        render={({ field: { onChange, onBlur, value }, formState }) =>
          render(
            value,
            onChange,
            onBlur,
            getObjectFieldByPath(formState.defaultValues, name),
          )
        }
      />
    );
  }

  return render(value, onChange, onBlur);
}
